import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { View, Text, Image, TouchableOpacity, StyleSheet, TextInput, ScrollView, useWindowDimensions, Platform } from 'react-native';
import { 
  MdFavoriteBorder, 
  MdFavorite, 
  MdInsertComment,
  MdVisibility,
  MdAlternateEmail,
  MdSentimentSatisfiedAlt,
  MdImage,
  MdChatBubbleOutline,
  MdStarBorder,
  MdSend,
  MdClose
} from 'react-icons/md';
import { ORANGE_ACTION, getPresetAvatarAsset } from '@cubemeet/ui';
import { createComment, getCommentsWithReplies, likeComment, unlikeComment } from '@cubemeet/core/services/travelCommentService';
import { supabase } from '@cubemeet/core/services/supabase';
import { getAvatarSource } from '@cubemeet/core/utils/avatarImageMapper';
import { useTravelI18n } from '../modules/travel/useTravelI18n';

interface Comment {
  id: string;
  author_id: string;
  author_name: string;
  author_avatar?: string;
  author_badge?: 'author' | 'admin' | 'agency';
  content: string;
  created_at: string;
  location?: string;
  likes: number;
  replies: number;
  liked: boolean;
  parent_id?: string;
  replyList?: Comment[];
}

interface PostEngagementProps {
  post_id: string;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  liked?: boolean;
  onLike?: () => void;
  comments?: Comment[];
  postAuthor?: {
    id: string;
    display_name: string;
    avatar_url?: string;
  };
  onAddComment?: (content: string) => void;
  onCommentCountChange?: (count: number) => void;
}

/**
 * PostEngagement - 帖子评论和互动区
 * 显示点赞、浏览、收藏、评论等
 */
const PostEngagement: React.FC<PostEngagementProps> = ({
  post_id,
  viewCount,
  likeCount,
  commentCount,
  liked = false,
  onLike,
  comments = [],
  postAuthor,
  onAddComment,
  onCommentCountChange,
}) => {
  const { t, language } = useTravelI18n();
  const { width } = useWindowDimensions();
  const [commentInput, setCommentInput] = useState('');
  const [replyingTo, setReplyingTo] = useState<{ id: string; authorName: string } | null>(null);
  const [isLiked, setIsLiked] = useState(liked);
  const [currentLikeCount, setCurrentLikeCount] = useState(likeCount);
  const [loadedComments, setLoadedComments] = useState<Comment[]>(comments);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMentionPicker, setShowMentionPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<TextInput>(null);
  const inputSectionRef = useRef<View>(null);
  const [pendingImages, setPendingImages] = useState<{ id: string; file: File; previewUrl: string }[]>([]);
  const [lightboxState, setLightboxState] = useState<{ images: string[]; currentIndex: number } | null>(null);

  // Hide bottom nav when lightbox is open
  useEffect(() => {
    if (lightboxState && typeof document !== 'undefined') {
      const portal = document.getElementById('app-bottom-nav-portal');
      if (portal) portal.style.setProperty('display', 'none', 'important');
      
      const elements = document.getElementsByClassName('app-bottom-nav-root');
      for (let i = 0; i < elements.length; i++) {
        (elements[i] as HTMLElement).style.setProperty('display', 'none', 'important');
      }

      return () => {
        if (portal) portal.style.removeProperty('display');
        for (let i = 0; i < elements.length; i++) {
          (elements[i] as HTMLElement).style.removeProperty('display');
        }
      };
    }
  }, [!!lightboxState]);

  // 计算可提及的用户列表（作者 + 评论区参与者，去重）
  const mentionableUsers = React.useMemo(() => {
    const users = new Map<string, { id: string; display_name: string; avatar_url?: string }>();
    
    // 1. 添加作者
    if (postAuthor) {
      users.set(postAuthor.id, {
        id: postAuthor.id,
        display_name: postAuthor.display_name,
        avatar_url: postAuthor.avatar_url
      });
    }

    // 2. 添加评论区用户
    loadedComments.forEach(comment => {
      if (!users.has(comment.author_id)) {
        users.set(comment.author_id, {
          id: comment.author_id,
          display_name: comment.author_name,
          avatar_url: comment.author_avatar
        });
      }
    });

    return Array.from(users.values());
  }, [postAuthor, loadedComments]);

  // Sync state with props
  useEffect(() => {
    setIsLiked(liked);
  }, [liked]);

  useEffect(() => {
    setCurrentLikeCount(likeCount);
  }, [likeCount]);

  // Focus input when replying
  useEffect(() => {
    if (replyingTo && textInputRef.current) {
      textInputRef.current.focus();
      // Scroll to input section
      if (inputSectionRef.current && Platform.OS === 'web') {
        try {
          const node = ReactDOM.findDOMNode(inputSectionRef.current) as HTMLElement;
          if (node && node.scrollIntoView) {
            node.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } catch (e) {}
      }
    }
  }, [replyingTo]);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  // 检查用户认证
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        console.log(t('travelPostDetail.postEngagement.auth.authenticated'), user.id);
        setIsAuthenticated(true);
        setCurrentUserId(user.id);
        setAuthError(null);
      } else {
        console.log(t('travelPostDetail.postEngagement.auth.unauthenticated'));
        setIsAuthenticated(false);
        setCurrentUserId(null);
        setAuthError(t('travelPostDetail.postEngagement.auth.pleaseLogin'));
      }
    } catch (error) {
      console.error(t('travelPostDetail.postEngagement.auth.checkFailed'), error);
      setAuthError(t('travelPostDetail.postEngagement.auth.checkFailed'));
    }
  };

  // 加载评论
  useEffect(() => {
    loadComments(1);
  }, [post_id]);

  const loadComments = async (pageToLoad = 1) => {
    try {
      if (pageToLoad === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const { data, count } = await getCommentsWithReplies('post', post_id, {
        page: pageToLoad,
        pageSize: 10,
      });
      
      const mapComment = (comment: any): Comment => ({
        id: comment.id,
        author_id: comment.author_id,
        author_name: comment.author?.display_name || t('travelPostDetail.postEngagement.comment.anonymous'),
        author_avatar: comment.author?.avatar_url,
        author_badge: comment.author_badge,
        content: comment.content,
        created_at: comment.created_at,
        location: comment.location,
        likes: comment.like_count || 0,
        replies: comment.replies?.length || 0,
        liked: comment.liked || false,
        parent_id: comment.parent_id,
        replyList: comment.replies?.map((r: any) => mapComment(r)) || [],
      });
      
      // 映射数据
      const mappedComments = (data || []).map(mapComment);
      
      if (pageToLoad === 1) {
        setLoadedComments(mappedComments);
      } else {
        setLoadedComments(prev => [...prev, ...mappedComments]);
      }
      
      setHasMore(mappedComments.length >= 10);
      setPage(pageToLoad);
      
      // 回调父组件更新评论数
      if (onCommentCountChange && pageToLoad === 1) {
        onCommentCountChange(count || mappedComments.length);
      }
    } catch (error) {
      console.error('加载评论失败:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
    setCurrentLikeCount(isLiked ? currentLikeCount - 1 : currentLikeCount + 1);
    if (onLike) {
      onLike();
    }
  };

  const handleCommentLike = async (commentId: string, isLiked: boolean) => {
    if (!isAuthenticated) {
      alert(t('travelPostDetail.postEngagement.auth.pleaseLogin'));
      return;
    }

    // Optimistic update
    const updateComments = (list: Comment[]): Comment[] => {
      return list.map(c => {
        if (c.id === commentId) {
          return { ...c, liked: !isLiked, likes: c.likes + (isLiked ? -1 : 1) };
        }
        if (c.replyList) {
          return { ...c, replyList: updateComments(c.replyList) };
        }
        return c;
      });
    };

    setLoadedComments(prev => updateComments(prev));

    try {
      if (isLiked) {
        await unlikeComment(commentId);
      } else {
        await likeComment(commentId);
      }
    } catch (error) {
      console.error('Failed to toggle comment like:', error);
      // Revert on error
      setLoadedComments(prev => updateComments(prev)); // This will flip it back if we call it again with the *new* state, but here we need to revert to *original* state.
      // Actually, since we flipped it, calling updateComments again with the *new* state (which is !isLiked) will flip it back to isLiked.
      // But wait, updateComments uses the `isLiked` parameter passed to the function, not the current state of the comment.
      // So we need to call it with !isLiked to revert.
      setLoadedComments(prev => {
         const revertUpdate = (list: Comment[]): Comment[] => {
            return list.map(c => {
              if (c.id === commentId) {
                return { ...c, liked: isLiked, likes: c.likes + (isLiked ? 1 : -1) };
              }
              if (c.replyList) {
                return { ...c, replyList: revertUpdate(c.replyList) };
              }
              return c;
            });
         };
         return revertUpdate(prev);
      });
    }
  };

  const handleAddComment = async () => {
    const content = commentInput.trim();
    const hasImages = pendingImages.length > 0;

    if (!content && !hasImages) {
      console.log(t('travelPostDetail.postEngagement.comment.empty'));
      return;
    }

    if (!isAuthenticated) {
      alert(t('travelPostDetail.postEngagement.auth.pleaseLogin'));
      return;
    }

    try {
      console.log(t('travelPostDetail.postEngagement.comment.start'), content);
      setSubmitting(true);
      
      let finalContent = content;

      // Upload pending images
      if (hasImages) {
        const uploadResults = await Promise.all(pendingImages.map(img => uploadImageFile(img.file)));
        const successfulUrls = uploadResults.filter(url => url !== null);
        
        if (successfulUrls.length !== pendingImages.length) {
           // Optional: warn user about partial failure
           console.warn('Some images failed to upload');
        }
        
        successfulUrls.forEach(url => {
          if (url) finalContent += ` [image:${url}] `;
        });
      }

      if (!finalContent.trim()) {
         // If only images were attempted and all failed
         alert(t('travelPostDetail.postEngagement.comment.failed'));
         return;
      }

      const result = await createComment({
        content: finalContent,
        target_type: 'post',
        target_id: post_id,
        parent_id: replyingTo?.id,
      });
      
      console.log(t('travelPostDetail.postEngagement.comment.success'), result);

      // 清空输入框和图片
      setCommentInput('');
      setPendingImages([]);
      setReplyingTo(null);

      // 重新加载评论
      console.log(t('travelPostDetail.postEngagement.comment.reload'));
      await loadComments(1);

      if (onAddComment) {
        onAddComment(finalContent);
      }
    } catch (error) {
      console.error(t('travelPostDetail.postEngagement.comment.failed'), error);
      alert(`${t('travelPostDetail.postEngagement.comment.failed')}: ${error instanceof Error ? error.message : t('travelPostDetail.postEngagement.comment.unknownError')}`);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('travelPostDetail.postEngagement.time.justNow');
    if (diffMins < 60) return t('travelPostDetail.postEngagement.time.minsAgo', { count: diffMins });
    if (diffHours < 24) return t('travelPostDetail.postEngagement.time.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('travelPostDetail.postEngagement.time.daysAgo', { count: diffDays });
    
    return date.toLocaleDateString(language === 'zh-CN' ? 'zh-CN' : language === 'zh-TW' ? 'zh-TW' : language === 'ja' ? 'ja-JP' : language === 'ko' ? 'ko-KR' : 'en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  // 生成默认头像（根据用户名的首字母）
  const getDefaultAvatar = (name: string) => {
    const initial = name?.charAt(0)?.toUpperCase() || '?';
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F'];
    const colorIndex = (name?.charCodeAt(0) || 0) % colors.length;
    const bgColor = encodeURIComponent(colors[colorIndex]);
    const textColor = encodeURIComponent('ffffff');
    
    // 使用 ui-avatars 服务生成头像
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initial)}&background=${bgColor}&color=${textColor}&bold=true&size=32`;
  };

  const getAvatar = (avatarUrl?: string, name?: string) => {
    if (avatarUrl?.startsWith('preset_')) {
      const asset = getPresetAvatarAsset(avatarUrl);
      if (asset) return asset;
    }
    return getAvatarSource(avatarUrl) || { uri: getDefaultAvatar(name || '?') };
  };

  const getBadgeColor = (badge?: string) => {
    switch (badge) {
      case 'author':
        return '#ff6b35';
      case 'admin':
        return '#ff4444';
      case 'agency':
        return '#0ea5a4';
      default:
        return null;
    }
  };

  const getBadgeLabel = (badge?: string) => {
    switch (badge) {
      case 'author':
        return t('travelPostDetail.postEngagement.badge.author');
      case 'admin':
        return t('travelPostDetail.postEngagement.badge.admin');
      case 'agency':
        return t('travelPostDetail.postEngagement.badge.agency');
      default:
        return null;
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (!currentUserId) {
      alert(t('travelPostDetail.postEngagement.auth.pleaseLogin'));
      return;
    }

    const newImages: { id: string; file: File; previewUrl: string }[] = [];
    const maxImages = 9;
    const currentCount = pendingImages.length;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (currentCount + newImages.length >= maxImages) {
        alert(t('travelPostDetail.postEngagement.comment.maxImages') || 'Max 9 images allowed');
        break;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        alert((t('travelPostDetail.postEngagement.comment.imageTooLarge') || 'Image too large (max 5MB)') + `: ${file.name}`);
        continue;
      }

      newImages.push({
        id: Math.random().toString(36).substr(2, 9),
        file,
        previewUrl: URL.createObjectURL(file)
      });
    }
    
    setPendingImages(prev => [...prev, ...newImages]);
    
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingImage = (id: string) => {
    setPendingImages(prev => prev.filter(img => img.id !== id));
  };

  const uploadImageFile = async (file: File): Promise<string | null> => {
    if (!currentUserId) return null;
    
    const formData = new FormData();
    formData.append('file', file);
    // Ensure post_id is valid, fallback to 'temp' if missing (though it shouldn't be)
    const safePostId = post_id || 'temp';
    formData.append('folder', `travel-post/${safePostId}/comments`);
    formData.append('userId', currentUserId);
    
    // Use the correct endpoint that supports folder parameter
    // If the previous endpoint ignores folder, we might need to check if there's a different one
    // But based on context, we only have this one. 
    // We'll try to force the folder param.
    const CARD_IMAGE_UPLOAD_ENDPOINT = (typeof window !== 'undefined' && (window as any).__CARD_IMAGE_UPLOAD__) || 'https://card-image-upload.cubemeet.app';
    
    try {
      const res = await fetch(CARD_IMAGE_UPLOAD_ENDPOINT, {
        method: 'POST',
        body: formData,
        mode: 'cors',
      });
      
      if (!res.ok) {
        throw new Error(`Upload failed: ${res.status}`);
      }
      
      const result = await res.json();
      return result.url || result.secure_url || (result.data && result.data.url);
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    setCommentInput(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handleMention = () => {
    setShowMentionPicker(!showMentionPicker);
    setShowEmojiPicker(false);
  };

  const handleMentionSelect = (user: { display_name: string }) => {
    // Add newline if input is not empty and doesn't end with newline
    const prefix = commentInput && !commentInput.endsWith('\n') ? '\n' : '';
    setCommentInput(prev => prev + `${prefix}@${user.display_name}\n`);
    setShowMentionPicker(false);
  };

  const renderCommentContent = (content: string) => {
    // Extract images
    const imageRegex = /\[image:([^\]]+)\]/g;
    const images: string[] = [];
    let match;
    while ((match = imageRegex.exec(content)) !== null) {
      images.push(match[1]);
    }

    // Remove images from text
    const textContent = content.replace(imageRegex, '').trim();

    // Render text with mentions
    const renderText = () => {
      if (!textContent) return null;
      
      // Split by newlines to handle explicit line breaks
      const lines = textContent.split('\n');
      
      return (
        <View style={{ marginBottom: images.length > 0 ? 8 : 0 }}>
          {lines.map((line, lineIndex) => {
             // Split by mention
             const parts = line.split(/(@[^\s]+)/g);
             return (
               <Text key={`line-${lineIndex}`} style={[styles.commentContent, { marginBottom: 0 }]}>
                 {parts.map((part, partIndex) => {
                   if (part.startsWith('@')) {
                     return (
                       <Text key={`mention-${partIndex}`} style={{ color: '#0056b3', fontWeight: '500' }}>
                         {part}
                       </Text>
                     );
                   }
                   return <Text key={`plain-${partIndex}`}>{part}</Text>;
                 })}
               </Text>
             );
          })}
        </View>
      );
    };

    // Render image grid
    const renderImages = () => {
      if (images.length === 0) return null;
      
      const isWide = width >= 768;

      return (
        <View style={{ 
          flexDirection: 'row', 
          flexWrap: 'wrap', 
          gap: 4,
          maxWidth: isWide ? 400 : '100%'
        }}>
          {images.map((url, index) => (
            <TouchableOpacity 
              key={`img-${index}`}
              activeOpacity={0.9}
              onPress={() => setLightboxState({ images, currentIndex: index })}
              style={{ 
                width: '23%', 
                aspectRatio: 1, 
                marginBottom: 4,
                borderRadius: 8,
                overflow: 'hidden',
                backgroundColor: '#f0f0f0'
              }}
            >
              <Image
                source={{ uri: url }}
                style={{ width: '100%', height: '100%', resizeMode: 'cover' }}
              />
            </TouchableOpacity>
          ))}
        </View>
      );
    };

    return (
      <View>
        {renderText()}
        {renderImages()}
      </View>
    );
  };

  const EMOJI_LIST = ['👍', '❤️', '😂', '😮', '😢', '😡', '🎉', '🔥', '👏', '🙏'];

  // Intersection Observer for infinite scroll
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          loadComments(page + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [loading, loadingMore, hasMore, page]);

  return (
    <View style={styles.container}>
      <input 
        type="file" 
        ref={fileInputRef} 
        style={{ display: 'none' }} 
        accept="image/*" 
        multiple
        onChange={handleImageSelect} 
      />

      {/* 互动按钮 + 统计 - 单行紧凑设计 */}
      <View style={styles.actionSection}>
        {/* 浏览 */}
        <View style={styles.actionItem}>
          <MdVisibility size={18} color="#666" />
          <Text style={styles.actionCount}>{viewCount}</Text>
        </View>

        <View style={styles.actionDivider} />

        {/* 点赞 */}
        <TouchableOpacity 
          style={styles.actionItem}
          onPress={handleLike}
          activeOpacity={0.7}
        >
          {isLiked ? (
            <MdFavorite size={20} color={ORANGE_ACTION} />
          ) : (
            <MdFavoriteBorder size={20} color="#666" />
          )}
          <Text style={[styles.actionCount, isLiked ? { color: ORANGE_ACTION } : undefined]}>{currentLikeCount}</Text>
        </TouchableOpacity>

        <View style={styles.actionDivider} />

        {/* 评论 */}
        <TouchableOpacity 
          style={styles.actionItem} 
          activeOpacity={0.7}
          onPress={() => {
            setReplyingTo(null);
            if (inputSectionRef.current && Platform.OS === 'web') {
              try {
                const node = ReactDOM.findDOMNode(inputSectionRef.current) as HTMLElement;
                if (node && node.scrollIntoView) {
                  node.scrollIntoView({ behavior: 'smooth', block: 'end' });
                }
                // Focus input if possible
                if (fileInputRef.current) {
                   // This is file input, we need text input ref. 
                   // But we don't have text input ref exposed easily here without another ref.
                   // Just scrolling is fine for now.
                }
              } catch (e) {}
            }
          }}
        >
          <MdInsertComment size={20} color="#666" />
          <Text style={styles.actionCount}>{commentCount}</Text>
        </TouchableOpacity>
      </View>

      {/* 评论输入 */}
      <View style={styles.commentInputSection} ref={inputSectionRef} nativeID="commentInputSection">
        <View style={styles.inputContainer}>
          <TextInput
            ref={textInputRef}
            style={styles.commentInput}
            placeholder={replyingTo 
              ? (t('travelPostDetail.postEngagement.comment.replyingTo', { name: replyingTo.authorName }) || `Replying to ${replyingTo.authorName}`)
              : (t('travelPostDetail.postEngagement.comment.placeholder') || 'Say something...')}
            placeholderTextColor="#ccc"
            value={commentInput}
            onChangeText={setCommentInput}
            multiline
            maxLength={500}
          />
          
          {/* Image Previews */}
          {pendingImages.length > 0 && (
            <ScrollView horizontal style={styles.previewList} contentContainerStyle={styles.previewContainer} showsHorizontalScrollIndicator={false}>
              {pendingImages.map(img => (
                <View key={img.id} style={styles.previewItem}>
                  <Image source={{ uri: img.previewUrl }} style={styles.previewImage} />
                  <TouchableOpacity style={styles.removePreviewButton} onPress={() => removePendingImage(img.id)}>
                    <MdClose size={12} color="#fff" />
                  </TouchableOpacity>
                </View>
              ))}
            </ScrollView>
          )}

          <View style={styles.inputActions}>
            <TouchableOpacity activeOpacity={0.7} onPress={handleMention}>
              <MdAlternateEmail size={20} color={showMentionPicker ? ORANGE_ACTION : "#999"} />
            </TouchableOpacity>
            <TouchableOpacity activeOpacity={0.7} onPress={() => { setShowEmojiPicker(!showEmojiPicker); setShowMentionPicker(false); }}>
              <MdSentimentSatisfiedAlt size={20} color={showEmojiPicker ? ORANGE_ACTION : "#999"} />
            </TouchableOpacity>
            <TouchableOpacity activeOpacity={0.7} onPress={() => fileInputRef.current?.click()}>
              <MdImage size={20} color="#999" />
            </TouchableOpacity>
            {(!!commentInput.trim() || pendingImages.length > 0) && (
              <TouchableOpacity 
                activeOpacity={0.7} 
                onPress={handleAddComment}
                disabled={submitting || !isAuthenticated}
              >
                <MdSend size={20} color={submitting ? "#ccc" : ORANGE_ACTION} />
              </TouchableOpacity>
            )}
          </View>
          
          {/* Mention Picker */}
          {showMentionPicker && (
            <View style={styles.emojiPicker}>
              <Text style={styles.pickerTitle}>{t('travelPostDetail.postEngagement.mention.title') || '提及用户'}</Text>
              <ScrollView style={{ maxHeight: 150 }} showsVerticalScrollIndicator={true}>
                {mentionableUsers.length > 0 ? (
                  mentionableUsers.map(user => (
                    <TouchableOpacity key={user.id} onPress={() => handleMentionSelect(user)} style={styles.mentionItem}>
                      <Image 
                        source={getAvatar(user.avatar_url, user.display_name)} 
                        style={styles.mentionAvatar} 
                      />
                      <Text style={styles.mentionName} numberOfLines={1}>{user.display_name}</Text>
                      {postAuthor && user.id === postAuthor.id && (
                        <Text style={styles.mentionBadge}>{t('travelPostDetail.postEngagement.badge.author') || '作者'}</Text>
                      )}
                    </TouchableOpacity>
                  ))
                ) : (
                  <Text style={styles.emptyText}>{t('travelPostDetail.postEngagement.mention.empty') || '暂无用户可提及'}</Text>
                )}
              </ScrollView>
            </View>
          )}

          {/* Emoji Picker */}
          {showEmojiPicker && (
            <View style={styles.emojiPicker}>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {EMOJI_LIST.map(emoji => (
                  <TouchableOpacity key={emoji} onPress={() => handleEmojiSelect(emoji)} style={styles.emojiItem}>
                    <Text style={styles.emojiText}>{emoji}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          )}
        </View>

        {authError && commentInput.trim() && (
          <Text style={styles.authErrorText}>{authError}</Text>
        )}
      </View>

      {/* 评论列表标题 */}
      {loadedComments.length > 0 && (
        <View style={styles.commentsHeader}>
          <Text style={styles.commentsTitle}>{t('travelPostDetail.postEngagement.comment.total', { count: loadedComments.length })}</Text>
        </View>
      )}

      {/* 加载状态 */}
      {loading && (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>{t('travelPostDetail.postEngagement.comment.loading')}</Text>
        </View>
      )}

      {/* 评论列表 */}
      <View style={styles.commentsList}>
        {loadedComments.map((comment) => (
          <View key={comment.id} style={styles.commentItem}>
            {/* 评论者头像和基本信息 */}
            <View style={styles.commentHeader}>
              <Image
                source={getAvatar(comment.author_avatar, comment.author_name)}
                style={styles.commentAvatar}
                onError={() => console.log(t('travelPostDetail.postEngagement.comment.avatarError'))}
              />
              <View style={styles.commentAuthorInfo}>
                <View style={styles.authorNameRow}>
                  <Text style={styles.commentAuthorName}>{comment.author_name}</Text>
                  {comment.author_badge && (
                    <Text
                      style={[
                        styles.authorBadge,
                        { color: getBadgeColor(comment.author_badge) },
                      ]}
                    >
                      {getBadgeLabel(comment.author_badge)}
                    </Text>
                  )}
                </View>
                <Text style={styles.commentDate}>
                  {`${formatDate(comment.created_at)}${comment.location ? ` · ${comment.location}` : ''}`}
                </Text>
              </View>
            </View>

            {/* 评论内容 */}
            {renderCommentContent(comment.content)}

            {/* 评论操作 */}
            <View style={styles.commentActions}>
              <TouchableOpacity 
                style={styles.commentActionItem} 
                activeOpacity={0.7}
                onPress={() => handleCommentLike(comment.id, comment.liked)}
              >
                {comment.liked ? (
                  <MdFavorite size={14} color={ORANGE_ACTION} />
                ) : (
                  <MdFavoriteBorder size={14} color="#999" />
                )}
                <Text style={[styles.commentActionCount, comment.liked && { color: ORANGE_ACTION }]}>{comment.likes}</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={styles.commentActionItem} 
                activeOpacity={0.7}
                onPress={() => {
                  setReplyingTo({ id: comment.id, authorName: comment.author_name });
                }}
              >
                <MdChatBubbleOutline size={14} color="#999" />
                <Text style={styles.commentActionCount}>{comment.replies}</Text>
              </TouchableOpacity>
            </View>

            {/* 二级评论渲染 */}
            {comment.replyList && comment.replyList.length > 0 && (
              <View style={styles.repliesContainer}>
                {comment.replyList.map((reply) => (
                  <View key={reply.id} style={styles.replyItem}>
                    <View style={styles.commentHeader}>
                      <Image
                        source={getAvatar(reply.author_avatar, reply.author_name)}
                        style={[styles.commentAvatar, { width: 24, height: 24, borderRadius: 12 }]}
                        onError={() => console.log(t('travelPostDetail.postEngagement.comment.avatarError'))}
                      />
                      <View style={styles.commentAuthorInfo}>
                        <View style={styles.authorNameRow}>
                          <Text style={[styles.commentAuthorName, { fontSize: 12 }]}>{reply.author_name}</Text>
                          {reply.author_badge && (
                            <Text
                              style={[
                                styles.authorBadge,
                                { color: getBadgeColor(reply.author_badge), fontSize: 10 },
                              ]}
                            >
                              {getBadgeLabel(reply.author_badge)}
                            </Text>
                          )}
                        </View>
                        <Text style={[styles.commentDate, { fontSize: 11 }]}>
                          {`${formatDate(reply.created_at)}${reply.location ? ` · ${reply.location}` : ''}`}
                        </Text>
                      </View>
                    </View>
                    
                    {renderCommentContent(reply.content)}

                    <View style={styles.commentActions}>
                      <TouchableOpacity 
                        style={styles.commentActionItem} 
                        activeOpacity={0.7}
                        onPress={() => handleCommentLike(reply.id, reply.liked)}
                      >
                        {reply.liked ? (
                          <MdFavorite size={12} color={ORANGE_ACTION} />
                        ) : (
                          <MdFavoriteBorder size={12} color="#999" />
                        )}
                        <Text style={[styles.commentActionCount, { fontSize: 11 }, reply.liked && { color: ORANGE_ACTION }]}>{reply.likes}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity 
                        style={styles.commentActionItem} 
                        activeOpacity={0.7}
                        onPress={() => {
                          setReplyingTo({ id: comment.id, authorName: reply.author_name });
                        }}
                      >
                        <MdChatBubbleOutline size={12} color="#999" />
                      </TouchableOpacity>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </View>
        ))}
      </View>

      {/* 加载更多 / 懒加载触发器 */}
      {hasMore && (
        <div ref={observerTarget} style={{ height: 20, width: '100%' }} />
      )}
      
      {loadingMore && (
        <View style={{ padding: 10, alignItems: 'center' }}>
          <Text style={{ color: '#999', fontSize: 12 }}>{t('travelPostDetail.postEngagement.comment.loading')}</Text>
        </View>
      )}

      {/* Lightbox Portal */}
      {lightboxState && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.9)',
            zIndex: 2147483647,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none'
          }}
          onClick={() => setLightboxState(null)}
        >
          <img 
            src={lightboxState.images[lightboxState.currentIndex]} 
            style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }} 
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking image
          />
          
          {/* Close button */}
          <div 
            style={{ position: 'absolute', top: 20, right: 20, cursor: 'pointer', padding: 10 }}
            onClick={() => setLightboxState(null)}
          >
            <MdClose size={30} color="#fff" />
          </div>

          {/* Navigation */}
          {lightboxState.images.length > 1 && (
            <>
              <div 
                style={{ position: 'absolute', left: 20, cursor: 'pointer', padding: 20 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxState(prev => prev ? ({ ...prev, currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length }) : null);
                }}
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </div>
              <div 
                style={{ position: 'absolute', right: 20, cursor: 'pointer', padding: 20 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxState(prev => prev ? ({ ...prev, currentIndex: (prev.currentIndex + 1) % prev.images.length }) : null);
                }}
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
  },

  // ===== 互动按钮区 =====
  actionSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },

  actionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 12,
  },

  actionIcon: {
    fontSize: 16,
  },

  actionCount: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
  },

  actionDivider: {
    width: 1,
    height: 14,
    backgroundColor: '#e0e0e0',
  },

  // ===== 评论输入区 =====
  commentInputSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },

  inputContainer: {
    flexDirection: 'column',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#f9f9f9',
    borderRadius: 6,
    borderWidth: 0,
    borderColor: 'transparent',
    outlineWidth: 0,
  },

  commentInput: {
    flex: 1,
    width: '100%',
    fontSize: 13,
    color: '#333',
    maxHeight: 80,
    paddingVertical: 0,
    outlineWidth: 0,
    outlineStyle: 'none',
  },

  previewList: {
    flexGrow: 0,
    marginBottom: 4,
  },
  
  previewContainer: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },

  previewItem: {
    position: 'relative',
    width: 60,
    height: 60,
  },

  previewImage: {
    width: 60,
    height: 60,
    borderRadius: 6,
    resizeMode: 'cover',
    backgroundColor: '#eee',
  },

  removePreviewButton: {
    position: 'absolute',
    top: -6,
    right: -6,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#fff',
  },

  inputActions: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    alignSelf: 'flex-end',
    width: '100%',
    justifyContent: 'flex-end',
  },

  inputActionIcon: {
    fontSize: 14,
    color: '#999',
  },

  submitButton: {
    marginTop: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#0ea5a4',
    borderRadius: 4,
    alignSelf: 'flex-end',
  },

  submitButtonDisabled: {
    opacity: 0.6,
  },

  submitButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },

  authErrorText: {
    fontSize: 11,
    color: '#ff4444',
    marginTop: 4,
  },

  replyingToContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f0f9ff',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    marginBottom: 8,
    borderLeftWidth: 3,
    borderLeftColor: '#0ea5a4',
  },
  
  replyingToText: {
    fontSize: 12,
    color: '#0ea5a4',
    fontWeight: '500',
  },

  // ===== 加载状态 =====
  loadingContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    alignItems: 'center',
  },

  loadingText: {
    fontSize: 13,
    color: '#999',
  },

  // ===== 评论列表 =====
  commentsHeader: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },

  commentsTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
  },

  commentsList: {
    paddingHorizontal: 16,
  },

  commentItem: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },

  commentHeader: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },

  commentAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f0f0f0',
    resizeMode: 'cover',
  },

  commentAuthorInfo: {
    flex: 1,
  },

  authorNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 2,
  },

  commentAuthorName: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
  },

  authorBadge: {
    fontSize: 10,
    fontWeight: '600',
  },

  commentDate: {
    fontSize: 11,
    color: '#999',
  },

  commentContent: {
    fontSize: 13,
    color: '#333',
    lineHeight: 18,
    marginBottom: 8,
  },

  commentActions: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 8,
  },

  commentActionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },

  commentActionIcon: {
    fontSize: 14,
  },

  commentActionCount: {
    fontSize: 11,
    color: '#999',
  },

  repliesContainer: {
    marginTop: 12,
    paddingLeft: 12,
    borderLeftWidth: 2,
    borderLeftColor: '#f0f0f0',
  },

  replyItem: {
    marginBottom: 12,
  },

  loadMoreButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    alignItems: 'center',
  },

  loadMoreText: {
    fontSize: 13,
    color: '#0ea5a4',
    fontWeight: '500',
  },

  emojiPicker: {
    position: 'absolute',
    bottom: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    padding: 8,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#f0f0f0',
  },
  emojiItem: {
    padding: 8,
  },
  emojiText: {
    fontSize: 20,
  },
  
  pickerTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#999',
    marginBottom: 8,
    paddingHorizontal: 4,
  },
  mentionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#f5f5f5',
  },
  mentionAvatar: {
    width: 24,
    height: 24,
    borderRadius: 12,
    marginRight: 8,
    backgroundColor: '#f0f0f0',
  },
  mentionName: {
    fontSize: 13,
    color: '#333',
    flex: 1,
  },
  mentionBadge: {
    fontSize: 10,
    color: '#ff6b35',
    backgroundColor: '#fff0e6',
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 4,
  },
  emptyText: {
    fontSize: 12,
    color: '#999',
    padding: 8,
    textAlign: 'center',
  },
  
  fixedInputBar: {
    position: 'fixed' as any,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    zIndex: 1000,
    paddingBottom: 20, // Safe area
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 10,
    // Center on desktop if needed, but full width is safer for mobile
    maxWidth: 600,
    marginHorizontal: 'auto' as any,
    // Ensure it's centered relative to viewport on desktop
    ...(Platform.OS === 'web' ? {
      left: '50%',
      transform: 'translateX(-50%)',
      width: '100%',
    } : {}),
  },
});

export default PostEngagement;
